import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, ApiError } from '@/lib/api';

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  locale: string;
  role: 'user' | 'staff';
  hasGoogle: boolean;
}

export interface ServerConfig {
  version: string;
  features: { google: boolean; magicLink: boolean; jira: boolean };
  retentionDays: number;
}

export const useSessionStore = defineStore('session', () => {
  const user = ref<SessionUser | null>(null);
  const config = ref<ServerConfig | null>(null);
  const loaded = ref(false);
  let loading: Promise<void> | null = null;

  const signedIn = computed(() => user.value !== null);
  const accountsEnabled = computed(() => Boolean(config.value?.features.google || config.value?.features.magicLink));

  async function fetchUser(): Promise<void> {
    try {
      user.value = (await api<{ user: SessionUser }>('/me')).user;
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) throw error;
      user.value = null;
    }
  }

  function load(force = false): Promise<void> {
    if (loading && !force) return loading;
    loading = (async () => {
      const [configResult] = await Promise.allSettled([api<ServerConfig>('/config'), fetchUser()]);
      if (configResult.status === 'fulfilled') config.value = configResult.value;
      loaded.value = true;
    })();
    return loading;
  }

  function setUser(value: SessionUser | null): void {
    user.value = value;
  }

  async function signOut(): Promise<void> {
    await api('/auth/logout', { method: 'POST', body: {} });
    user.value = null;
  }

  return { user, config, loaded, signedIn, accountsEnabled, load, setUser, signOut, fetchUser };
});
